import { useQuery } from "@tanstack/react-query";

import { profileKeys } from "../api/queryKeys";
import { getProfile } from "../api";
import { useAuth } from "@/features/auth";
import { setStoredProfileAvatarUrl } from "../utils/avatar";

import type { CombinedUserProfile } from "../schemas";

/**
 * Hook to fetch and combine user profile data.
 * Merges Supabase Auth user with backend profile.
 */
export function useProfile() {
	const { user } = useAuth();

	return useQuery<CombinedUserProfile>({
		queryKey: profileKeys.detail(user?.id),
		queryFn: async () => {
			if (!user) {
				throw new Error("User not authenticated");
			}

			const backendProfile = await getProfile();
			setStoredProfileAvatarUrl(backendProfile.avatar_url ?? null);

			const combined: CombinedUserProfile = {
				id: user.id,
				email: user.email || backendProfile.correo,
				full_name: user.user_metadata?.full_name,
				avatar_url: backendProfile.avatar_url ?? null,

				// Personal Information
				primer_nombre: backendProfile.primer_nombre,
				apellido: backendProfile.apellido,
				telefono: backendProfile.telefono,
				biografia: backendProfile.biografia,
				ocupacion: backendProfile.ocupacion,
				fecha_nacimiento: backendProfile.fecha_nacimiento,

				// Financial Profile
				credit_score_range: backendProfile.credit_score_range,
				debt_to_income_ratio: backendProfile.debt_to_income_ratio,
				first_time_homebuyer: backendProfile.first_time_homebuyer ?? false,
				pre_approval_status: backendProfile.pre_approval_status ?? "not_started",

				// System fields
				es_admin: backendProfile.es_admin,
				created_at: backendProfile.created_at,
				updated_at: backendProfile.updated_at,
			};

			return combined;
		},
		enabled: !!user,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
}
